/**
 * Chapi in motion (docs/designs/motion/): the designer's keyframes, timings and easing as data, so the web
 * (Web Animations API) and mobile (Reanimated) play the same animation from one source.
 *
 * A track is one CSS @keyframes block from docs/designs/motion/Chapi_in_Motion.html, applied to one layer.
 * Keys are at a fraction of the track's duration. Like CSS, the track's easing applies to every segment
 * between keys, and a property is interpolated only between the keys that set it.
 *
 * Units: translations on Chapi's layers are in the art's own units (the SVG view box), so they scale with
 * the drawing. Translations on HTML-level layers (bubbles, labels, confetti) are in px at the design size
 * named next to them; renderers scale them by `size / designSize`. `pct` tracks translate by a percentage
 * of the layer's own box (the wordmark wipe).
 */
/** CSS's named timing functions, and the ones the designer used. */
export const EASE = {
    ease: [0.25, 0.1, 0.25, 1],
    easeIn: [0.42, 0, 1, 1],
    easeOut: [0, 0, 0.58, 1],
    easeInOut: [0.42, 0, 0.58, 1],
    /** Logo leaves fly in and settle. */
    settle: [0.2, 0.8, 0.2, 1],
    /** Loading mark: a slow start and finish to each turn. */
    turn: [0.65, 0.05, 0.35, 0.95],
    /** Task done: the hop. */
    hop: [0.3, 0.7, 0.4, 1],
    /** Confetti flies out fast and drifts. */
    burst: [0.15, 0.7, 0.3, 1],
    /** Pop with an overshoot (the sent bubble). */
    lift: [0.3, 1.4, 0.5, 1],
    /** Chapi pops in, a bigger overshoot. */
    pop: [0.3, 1.5, 0.5, 1],
    /** The status badge, the biggest overshoot. */
    badge: [0.3, 1.6, 0.5, 1],
};
export const IDENTITY = { x: 0, y: 0, sx: 1, sy: 1, r: 0 };
export function resolveTransform(t) {
    if (t === 0)
        return IDENTITY;
    const s = t.s ?? 1;
    return { x: t.x ?? 0, y: t.y ?? 0, sx: t.sx ?? s, sy: t.sy ?? s, r: t.r ?? 0 };
}
/** The value of a cubic-bezier timing function at progress `x` (0..1). */
export function bezierAt(ease, x) {
    if (x <= 0)
        return 0;
    if (x >= 1)
        return 1;
    if (ease === 'linear')
        return x;
    const [x1, y1, x2, y2] = ease;
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const sampleX = (u) => ((ax * u + bx) * u + cx) * u;
    let u = x;
    // Newton's method, then bisection if the slope is too flat.
    for (let i = 0; i < 8; i++) {
        const err = sampleX(u) - x;
        if (Math.abs(err) < 1e-6)
            return ((ay * u + by) * u + cy) * u;
        const slope = (3 * ax * u + 2 * bx) * u + cx;
        if (Math.abs(slope) < 1e-6)
            break;
        u -= err / slope;
    }
    let lo = 0, hi = 1;
    u = x;
    for (let i = 0; i < 40; i++) {
        const v = sampleX(u);
        if (Math.abs(v - x) < 1e-6)
            break;
        if (v < x)
            lo = u;
        else
            hi = u;
        u = (lo + hi) / 2;
    }
    return ((ay * u + by) * u + cy) * u;
}
function valueOf(k, prop) {
    if (prop === 't')
        return k.t === undefined ? undefined : resolveTransform(k.t);
    return prop === 'o' ? k.o : k.d;
}
function mix(a, b, p) {
    if (typeof a === 'number' && typeof b === 'number')
        return a + (b - a) * p;
    const A = a, B = b;
    return { x: A.x + (B.x - A.x) * p, y: A.y + (B.y - A.y) * p, sx: A.sx + (B.sx - A.sx) * p, sy: A.sy + (B.sy - A.sy) * p, r: A.r + (B.r - A.r) * p };
}
/** A property's value at progress `at`, the way CSS interpolates it (undefined if the track never sets it). */
export function sampleProp(track, prop, at) {
    const keys = track.keys.filter((k) => valueOf(k, prop) !== undefined);
    if (!keys.length)
        return undefined;
    const first = keys[0], last = keys[keys.length - 1];
    if (at <= first.at)
        return valueOf(first, prop);
    if (at >= last.at)
        return valueOf(last, prop);
    for (let i = 1; i < keys.length; i++) {
        const b = keys[i];
        if (at > b.at)
            continue;
        const a = keys[i - 1];
        const span = b.at - a.at;
        const p = span <= 0 ? 1 : bezierAt(track.ease, (at - a.at) / span);
        return mix(valueOf(a, prop), valueOf(b, prop), p);
    }
    return valueOf(last, prop);
}
function toKey(at, t, o, d) {
    const k = { at };
    if (t !== undefined) {
        const r = t;
        k.t = { x: r.x, y: r.y, sx: r.sx, sy: r.sy, r: r.r };
    }
    if (o !== undefined)
        k.o = o;
    if (d !== undefined)
        k.d = d;
    return k;
}
/**
 * The part of a looping design track between `from` and `to` (fractions of its loop), as a track of its
 * own that plays once. In-app moments play the interesting middle of each loop, not its idle lead-in or
 * the fade that resets the loop. Cut points between keys are sampled with the track's easing; keys inside
 * the slice keep the track's easing, so slices taken where the design rests are exact.
 */
export function sliceTrack(track, from, to) {
    const span = to - from;
    const inside = track.keys.filter((k) => k.at > from && k.at < to);
    const at = (x) => (x - from) / span;
    const keys = [
        toKey(0, sampleProp(track, 't', from), sampleProp(track, 'o', from), sampleProp(track, 'd', from)),
        ...inside.map((k) => ({ ...k, at: at(k.at) })),
        toKey(1, sampleProp(track, 't', to), sampleProp(track, 'o', to), sampleProp(track, 'd', to)),
    ];
    return { ...track, ms: Math.round(track.ms * span), keys };
}
/** The resting frame of a track: what reduced motion shows, and where a one-shot ends. */
export function stillFrame(track, at = 1) {
    return {
        t: sampleProp(track, 't', at) ?? IDENTITY,
        o: sampleProp(track, 'o', at) ?? 1,
        d: sampleProp(track, 'd', at) ?? 0,
    };
}
// ---------------------------------------------------------------------------------------------------
// The nine animations. Values copied from the design's CSS; comments name the @keyframes block.
// ---------------------------------------------------------------------------------------------------
const leafW = 357;
const leafH = 228;
/** 1. Logo reveal (leafA, leafB, word, dot; 5 s loop). Leaf and dot units are the logo's path units. */
export const logoReveal = {
    leafA: {
        ms: 5000,
        ease: EASE.settle,
        keys: [
            { at: 0, t: { x: leafW * 0.9, y: -leafH * 0.9, r: 40 }, o: 0 },
            { at: 0.06, o: 1 },
            { at: 0.2, t: 0 },
            { at: 0.24, t: { s: 1.08 } },
            { at: 0.3, t: 0 },
            { at: 1, t: 0, o: 1 },
        ],
    },
    leafB: {
        ms: 5000,
        ease: EASE.settle,
        keys: [
            { at: 0, t: { x: -leafW * 0.9, y: leafH * 0.9, r: -40 }, o: 0 },
            { at: 0.04, t: { x: -leafW * 0.9, y: leafH * 0.9, r: -40 }, o: 0 },
            { at: 0.1, o: 1 },
            { at: 0.24, t: 0 },
            { at: 0.28, t: { s: 1.08 } },
            { at: 0.34, t: 0 },
            { at: 1, t: 0, o: 1 },
        ],
    },
    /** The wordmark wipes in from the left: the clip window slides right while the word stays put. */
    word: {
        ms: 5000,
        ease: EASE.easeOut,
        pct: true,
        keys: [
            { at: 0, t: { x: -100 } },
            { at: 0.28, t: { x: -100 } },
            { at: 0.52, t: 0 },
            { at: 1, t: 0 },
        ],
    },
    dot: {
        ms: 5000,
        ease: EASE.ease,
        keys: [
            { at: 0, t: { y: -236 * 4.2 }, o: 0 },
            { at: 0.52, t: { y: -236 * 4.2 }, o: 0 },
            { at: 0.54, o: 1 },
            { at: 0.62, t: { y: 0, sx: 1.25, sy: 0.75 } },
            { at: 0.68, t: { y: -236 * 0.55, sx: 0.95, sy: 1.05 } },
            { at: 0.74, t: { y: 0, sx: 1.08, sy: 0.92 } },
            { at: 0.78, t: 0 },
            { at: 1, t: 0, o: 1 },
        ],
    },
};
/** The counter-move that keeps the word still inside its sliding clip window. */
export function counterTrack(track) {
    return {
        ...track,
        keys: track.keys.map((k) => (k.t === undefined || k.t === 0 ? k : { ...k, t: { ...k.t, x: -(k.t.x ?? 0), y: -(k.t.y ?? 0) } })),
    };
}
/**
 * The splash plays the reveal at twice the design's speed: the app is usually ready well before a 5 s
 * loop would finish, and a splash may linger at most `SPLASH_MAX_EXTRA_MS` once it is.
 */
export const SPLASH_SPEED = 2;
/** The reveal reads once the dot has landed (62% of the loop). */
export const LOGO_READABLE_MS = Math.round((5000 * 0.62) / SPLASH_SPEED);
export const SPLASH_MAX_EXTRA_MS = 1200;
/**
 * How much longer the splash stays once the app is ready: until the reveal reads, but never more than
 * `maxExtraMs`. Zero when reduced motion is on (there is nothing to watch) or the reveal already finished.
 */
export function splashHoldMs({ elapsedMs, reduced, readableMs = LOGO_READABLE_MS, maxExtraMs = SPLASH_MAX_EXTRA_MS }) {
    if (reduced)
        return 0;
    return Math.max(0, Math.min(maxExtraMs, readableMs - elapsedMs));
}
/** 2. Hello wave (wave 2.6 s, blink 4 s, float 3 s alternate). */
export const helloWave = {
    armR: {
        ms: 2600,
        ease: EASE.easeInOut,
        keys: [
            { at: 0, t: 0 },
            { at: 0.1, t: { r: -22 } },
            { at: 0.2, t: { r: 14 } },
            { at: 0.3, t: { r: -22 } },
            { at: 0.4, t: { r: 14 } },
            { at: 0.55, t: 0 },
            { at: 1, t: 0 },
        ],
    },
    eyes: blink(4000),
    float: { ms: 3000, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 1, t: { y: -8 } }] },
};
function blink(ms) {
    return {
        ms,
        ease: EASE.ease,
        keys: [
            { at: 0, t: 0 },
            { at: 0.44, t: 0 },
            { at: 0.48, t: { sy: 0.1 } },
            { at: 0.52, t: 0 },
            { at: 1, t: 0 },
        ],
    };
}
/** 3. Thinking (tilt 2.8 s, scratch 1.4 s alternate, blink 3.4 s, typing dots 1.2 s staggered 0.15 s). */
export const thinking = {
    head: { ms: 2800, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -6 } }, { at: 0.5, t: { r: 6 } }, { at: 1, t: { r: -6 } }] },
    /** Played forwards then backwards (CSS `alternate`). */
    armR: { ms: 1400, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: 18 } }, { at: 1, t: { r: 34 } }] },
    eyes: blink(3400),
    /** px at a 9 px dot. */
    dot: {
        ms: 1200,
        ease: EASE.easeInOut,
        keys: [
            { at: 0, t: 0, o: 0.5 },
            { at: 0.3, t: { y: -7 }, o: 1 },
            { at: 0.6, t: 0, o: 0.5 },
            { at: 1, t: 0, o: 0.5 },
        ],
    },
};
export const THINKING_DOT_SIZE = 9;
export const THINKING_DOT_STAGGER_MS = 150;
/** 4. Loading (spin 1.5 s, the two leaves part and regroup). Leaf units are path units. */
export const loading = {
    spin: { ms: 1500, ease: EASE.turn, keys: [{ at: 0, t: 0 }, { at: 1, t: { r: 360 } }] },
    leafA: { ms: 1500, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.5, t: { x: leafW * 0.12, y: -leafH * 0.12, s: 0.9 } }, { at: 1, t: 0 }] },
    leafB: { ms: 1500, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.5, t: { x: -leafW * 0.12, y: leafH * 0.12, s: 0.9 } }, { at: 1, t: 0 }] },
};
/** 5. Chapi pops in (popIn, wave2, blink, hiIn; 3.8 s loop). Pop units are art units. */
export const popIn = {
    /** 80 px at a 130 px wide Chapi, in art units (view box 154.22 wide). */
    root: {
        ms: 3800,
        ease: EASE.pop,
        keys: [
            { at: 0, t: { y: 80 * (154.22 / 130), s: 0.3 }, o: 0 },
            { at: 0.18, t: { y: 80 * (154.22 / 130), s: 0.3 }, o: 0 },
            { at: 0.32, t: 0, o: 1 },
            { at: 0.88, t: 0, o: 1 },
            { at: 0.96, t: { y: 20 * (154.22 / 130), s: 0.8 }, o: 0 },
            { at: 1, t: { y: 20 * (154.22 / 130), s: 0.8 }, o: 0 },
        ],
    },
    armR: {
        ms: 3800,
        ease: EASE.easeInOut,
        keys: [
            { at: 0, t: 0 },
            { at: 0.38, t: 0 },
            { at: 0.46, t: { r: -22 } },
            { at: 0.54, t: { r: 14 } },
            { at: 0.62, t: { r: -22 } },
            { at: 0.7, t: { r: 14 } },
            { at: 0.8, t: 0 },
            { at: 1, t: 0 },
        ],
    },
    eyes: blink(3800),
    /** The "How can I help" bubble; px. */
    hi: {
        ms: 3800,
        ease: EASE.ease,
        keys: [
            { at: 0, t: { y: 8 }, o: 0 },
            { at: 0.4, t: { y: 8 }, o: 0 },
            { at: 0.5, t: 0, o: 1 },
            { at: 0.88, t: 0, o: 1 },
            { at: 0.96, t: 0, o: 0 },
            { at: 1, t: 0, o: 0 },
        ],
    },
};
/** 6. Task done (jump, arms up, shadow, confetti; 3.6 s loop). Jump units are art units. */
export const taskDone = {
    whole: {
        ms: 3600,
        ease: EASE.hop,
        keys: [
            { at: 0, t: 0 },
            { at: 0.26, t: 0 },
            { at: 0.3, t: { sx: 1.06, sy: 0.9 } },
            { at: 0.4, t: { y: -46, sx: 0.96, sy: 1.05 } },
            { at: 0.52, t: { y: 0, sx: 1.06, sy: 0.92 } },
            { at: 0.58, t: 0 },
            { at: 1, t: 0 },
        ],
    },
    armL: { ms: 3600, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.28, t: 0 }, { at: 0.38, t: { r: 110 } }, { at: 0.6, t: { r: 110 } }, { at: 0.72, t: 0 }, { at: 1, t: 0 }] },
    armR: { ms: 3600, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.28, t: 0 }, { at: 0.38, t: { r: -14 } }, { at: 0.6, t: { r: -14 } }, { at: 1, t: 0 }] },
    shadow: { ms: 3600, ease: EASE.ease, keys: [{ at: 0, t: 0, o: 1 }, { at: 0.3, t: 0, o: 1 }, { at: 0.4, t: { s: 0.6 }, o: 0.5 }, { at: 0.52, t: 0, o: 1 }, { at: 1, t: 0, o: 1 }] },
    /** The tick's box fills and its check draws (boxfill, draw). */
    tick: { ms: 3600, ease: EASE.ease, keys: [{ at: 0, d: 1 }, { at: 0.14, d: 1 }, { at: 0.28, d: 0 }, { at: 1, d: 0 }] },
};
/** The 22 pieces from the design, in order. */
export const CONFETTI = [
    [79, -36, -251, 0, 10], [88, -3, -241, 1, 110], [67, 3, 128, 2, 10], [73, 31, 279, 3, 10], [55, 62, -237, 4, 70], [24, 96, -253, 5, 70],
    [0, 65, 253, 0, 10], [-41, 82, -115, 1, 10], [-55, 30, -201, 2, 70], [-54, 20, -90, 3, 60], [-112, 1, 176, 4, 70], [-88, -28, -116, 5, 80],
    [-103, -49, 237, 0, 60], [-85, -77, -226, 1, 10], [-78, -115, -145, 2, 110], [-57, -144, -221, 3, 90], [-14, -152, 21, 4, 40], [8, -129, 167, 5, 10],
    [23, -113, -234, 0, 10], [78, -106, 156, 1, 30], [90, -94, -277, 2, 110], [100, -66, 205, 3, 10],
].map(([x, y, r, c, delayMs]) => ({ x: x, y: y, r: r, c: c, delayMs: delayMs }));
/** A confetti piece's flight (conf): out, then a 40 px drop as it fades. */
export function confettiTrack(p) {
    return {
        ms: 3600,
        ease: EASE.burst,
        keys: [
            { at: 0, t: 0, o: 0 },
            { at: 0.3, t: 0, o: 0 },
            { at: 0.32, o: 1 },
            { at: 0.62, t: { x: p.x, y: p.y, r: p.r }, o: 1 },
            { at: 0.76, t: { x: p.x, y: p.y + 40, r: p.r }, o: 0 },
            { at: 1, t: { x: p.x, y: p.y + 40, r: p.r }, o: 0 },
        ],
    };
}
export const CONFETTI_PIECE = { width: 8, height: 13, radius: 2 };
/** The celebration plays this part of the 3.6 s loop: from just before the hop until the confetti has gone. */
export const TASK_DONE_SLICE = [0.26, 0.8];
/** 7. Send (bubbleIn, press, fly; 3.6 s loop). px at a 20 px plane. */
export const send = {
    bubble: {
        ms: 3600,
        ease: EASE.lift,
        keys: [
            { at: 0, t: { y: 26, s: 0.9 }, o: 0 },
            { at: 0.26, t: { y: 26, s: 0.9 }, o: 0 },
            { at: 0.38, t: 0, o: 1 },
            { at: 0.86, t: 0, o: 1 },
            { at: 0.96, t: 0, o: 0 },
            { at: 1, t: 0, o: 0 },
        ],
    },
    press: { ms: 3600, ease: EASE.ease, keys: [{ at: 0, t: 0 }, { at: 0.16, t: 0 }, { at: 0.22, t: { s: 0.86 } }, { at: 0.3, t: 0 }, { at: 1, t: 0 }] },
    plane: {
        ms: 3600,
        ease: EASE.ease,
        keys: [
            { at: 0, t: 0, o: 1 },
            { at: 0.22, t: 0, o: 1 },
            { at: 0.32, t: { x: 28, y: -28 }, o: 0 },
            { at: 0.33, t: { x: -22, y: 22 }, o: 0 },
            { at: 0.44, t: 0, o: 1 },
            { at: 1, t: 0, o: 1 },
        ],
    },
};
export const SEND_PLANE_SIZE = 20;
/** The press and the plane, from the tap until the plane is back. */
export const SEND_SLICE = [0.16, 0.44];
/** The message lifting out of the composer. */
export const SEND_BUBBLE_SLICE = [0.26, 0.38];
/** 8. Status set (ring, badge, label; 4 s loop). */
export const statusSet = {
    ring: { ms: 4000, ease: EASE.ease, keys: [{ at: 0, d: 1 }, { at: 0.08, d: 1 }, { at: 0.34, d: 0 }, { at: 0.88, d: 0 }, { at: 0.96, d: 1 }, { at: 1, d: 1 }] },
    badge: { ms: 4000, ease: EASE.badge, keys: [{ at: 0, t: { s: 0 } }, { at: 0.32, t: { s: 0 } }, { at: 0.44, t: 0 }, { at: 0.88, t: 0 }, { at: 0.96, t: { s: 0 } }, { at: 1, t: { s: 0 } }] },
    /** px at the design size. */
    label: { ms: 4000, ease: EASE.ease, keys: [{ at: 0, t: { y: 8 }, o: 0 }, { at: 0.42, t: { y: 8 }, o: 0 }, { at: 0.54, t: 0, o: 1 }, { at: 0.88, t: 0, o: 1 }, { at: 0.96, t: 0, o: 0 }, { at: 1, t: 0, o: 0 }] },
};
/** From the ring starting to draw until the label has settled. */
export const STATUS_SLICE = [0.08, 0.54];
/** 9. All up to date (breathe 3.2 s, zz 3.6 s staggered 1.2 s). */
export const caughtUp = {
    breathe: { ms: 3200, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.5, t: { sx: 1.02, sy: 1.04 } }, { at: 1, t: 0 }] },
    /** px at a 150 px Chapi. */
    z: { ms: 3600, ease: EASE.easeOut, keys: [{ at: 0, t: { x: 0, y: 10, r: -10 }, o: 0 }, { at: 0.2, o: 1 }, { at: 1, t: { x: 22, y: -50, r: 12 }, o: 0 }] },
};
/** The three z's: position (fraction of the drawing), font size (px at 150 px), letter and start. */
export const SLEEP_ZS = [
    { left: 0.62, top: 0.08, size: 18, letter: 'z', delayMs: 0 },
    { left: 0.7, top: 0.02, size: 24, letter: 'z', delayMs: 700 },
    { left: 0.78, top: -0.06, size: 30, letter: 'Z', delayMs: 1400 },
];
/**
 * Motion that starts by itself stops within five seconds (WCAG 2.2.2), then rests on its still frame.
 * Loading and thinking are exempt while the work they show is still going.
 */
export const AUTOPLAY_LIMIT_MS = 5000;
/** How many whole times a looping track can play inside the autoplay limit (at least once). */
export function playsWithinLimit(track, delayMs = 0, limitMs = AUTOPLAY_LIMIT_MS) {
    return Math.max(1, Math.floor((limitMs - delayMs) / track.ms));
}
// ---------------------------------------------------------------------------------------------------
// Status and work updates (the second set): shown on someone's status circle, in notifications and in
// chat when things change. They play when something changes, a few times at most, never across a list.
// ---------------------------------------------------------------------------------------------------
/** The status pill under a status moment (pillIn, 4 s); px. */
export const statusPill = {
    ms: 4000,
    ease: EASE.ease,
    keys: [{ at: 0, t: { y: 8 }, o: 0 }, { at: 0.1, t: { y: 8 }, o: 0 }, { at: 0.22, t: 0, o: 1 }, { at: 0.9, t: 0, o: 1 }, { at: 1, t: 0, o: 0 }],
};
export const STATUS_PILL_SLICE = [0.1, 0.22];
const dropTrack = (ms) => ({
    ms,
    ease: EASE.easeIn,
    keys: [{ at: 0, t: 0, o: 0 }, { at: 0.3, t: 0, o: 0 }, { at: 0.4, o: 1 }, { at: 0.9, t: { y: 26 }, o: 0 }, { at: 1, t: { y: 26 }, o: 0 }],
});
/** Task delayed (worry: tilt 3 s, scratch 1.1 s alternate, blink 3 s, sweat 2.4 s; the due chip 4 s). */
export const taskDelayed = {
    head: { ms: 3000, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -6 } }, { at: 0.5, t: { r: 6 } }, { at: 1, t: { r: -6 } }] },
    armR: { ms: 1100, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: 18 } }, { at: 1, t: { r: 34 } }] },
    eyes: blink(3000),
    /** px at a 130 px Chapi. */
    sweat: dropTrack(2400),
    /** The card shakes as the date slips (px). */
    shake: {
        ms: 4000,
        ease: EASE.ease,
        keys: [{ at: 0, t: 0 }, { at: 0.42, t: 0 }, { at: 0.45, t: { x: -6 } }, { at: 0.48, t: { x: 6 } }, { at: 0.51, t: { x: -4 } }, { at: 0.54, t: { x: 4 } }, { at: 0.6, t: 0 }, { at: 1, t: 0 }],
    },
    /** "Due Thu" gives way to "1 day overdue" (swapA, swapB; the chip turns red with it, dueC). */
    dueOut: { ms: 4000, ease: EASE.ease, keys: [{ at: 0, o: 1 }, { at: 0.4, o: 1 }, { at: 0.44, o: 0 }, { at: 1, o: 0 }] },
    dueIn: { ms: 4000, ease: EASE.ease, keys: [{ at: 0, o: 0 }, { at: 0.38, o: 0 }, { at: 0.46, o: 1 }, { at: 1, o: 1 }] },
    /** The clock's hand races (spin 1 s linear). */
    hand: { ms: 1000, ease: 'linear', keys: [{ at: 0, t: 0 }, { at: 1, t: { r: 360 } }] },
};
/** From the moment the date slips until the card is still again. */
export const TASK_DELAYED_SLICE = [0.36, 0.6];
/** Off sick (shiver 0.18 s alternate, slow blink 3 s, flushed cheeks 1.6 s, a drip 2.6 s, the thermometer). */
export const offSick = {
    shiver: { ms: 180, ease: 'linear', keys: [{ at: 0, t: { x: -1 } }, { at: 1, t: { x: 1 } }] },
    eyes: {
        ms: 3000,
        ease: EASE.ease,
        keys: [{ at: 0, t: { sy: 0.55 } }, { at: 0.4, t: { sy: 0.55 } }, { at: 0.5, t: { sy: 0.15 } }, { at: 0.6, t: { sy: 0.15 } }, { at: 0.7, t: { sy: 0.55 } }, { at: 1, t: { sy: 0.55 } }],
    },
    flush: { ms: 1600, ease: EASE.easeInOut, keys: [{ at: 0, o: 0.45 }, { at: 0.5, o: 0.85 }, { at: 1, o: 0.45 }] },
    drip: dropTrack(2600),
    temp: { ms: 4000, ease: EASE.ease, keys: [{ at: 0, t: { s: 0.6 }, o: 0 }, { at: 0.12, t: { s: 0.6 }, o: 0 }, { at: 0.22, t: 0, o: 1 }, { at: 0.9, t: 0, o: 1 }, { at: 1, t: 0, o: 0 }] },
    mercury: { ms: 3000, ease: EASE.easeInOut, keys: [{ at: 0, t: { sy: 0.35 } }, { at: 0.6, t: { sy: 1 } }, { at: 1, t: { sy: 0.35 } }] },
};
/** The sick pose's resting eyes are half shut. */
export const SICK_EYES_REST = 0.55;
/** On holiday (sway 3 s, the sun's rays turn 12 s). */
export const onHoliday = {
    sway: { ms: 3000, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -3 } }, { at: 0.5, t: { r: 3 } }, { at: 1, t: { r: -3 } }] },
    rays: { ms: 12000, ease: 'linear', keys: [{ at: 0, t: 0 }, { at: 1, t: { r: 360 } }] },
};
/** In a meeting (talk 1.6 s, the voice bars 0.9 s staggered 0.15 s, the live dot 1.2 s). */
export const inMeeting = {
    talk: { ms: 1600, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.25, t: { y: -3, r: -1.5 } }, { at: 0.75, t: { y: -2, r: 1.5 } }, { at: 1, t: 0 }] },
    bar: { ms: 900, ease: EASE.easeInOut, keys: [{ at: 0, t: { sy: 0.25 } }, { at: 0.5, t: { sy: 1 } }, { at: 1, t: { sy: 0.25 } }] },
    /** The live dot's ring, as a scale and fade (the design pulses a box-shadow). */
    live: { ms: 1200, ease: EASE.ease, keys: [{ at: 0, t: { s: 1 }, o: 0.5 }, { at: 0.5, t: { s: 1.75 }, o: 0 }, { at: 1, t: { s: 1.75 }, o: 0 }] },
};
export const MEETING_BAR_STAGGER_MS = 150;
/**
 * The small badge on a status circle is 16 px, where the full pose's few degrees would not show, so the
 * badge carries the same motion, larger. It plays when the status changes or on hover, a few times.
 */
export const statusBadge = {
    sick: { track: { ms: 180, ease: 'linear', keys: [{ at: 0, t: { x: -1 } }, { at: 1, t: { x: 1 } }] }, iterations: 10, alternate: true },
    holiday: { track: { ms: 1500, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.25, t: { r: -14 } }, { at: 0.75, t: { r: 14 } }, { at: 1, t: 0 }] }, iterations: 2, alternate: false },
    meeting: { track: { ms: 1600, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.25, t: { y: -2, r: -8 } }, { at: 0.75, t: { y: -1.5, r: 8 } }, { at: 1, t: 0 }] }, iterations: 2, alternate: false },
};
export const STATUS_MOMENT_PRESETS = Object.keys(statusBadge);
/** Birthday (hop 1.2 s, banner 4.4 s, balloons rise 6 s, the candle flickers 0.25 s). Built, not yet wired. */
export const birthday = {
    hop: { ms: 1200, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.4, t: { y: -10, sx: 0.98, sy: 1.02 } }, { at: 0.6, t: { y: 0, sx: 1.03, sy: 0.97 } }, { at: 1, t: 0 }] },
    banner: { ms: 4400, ease: [0.3, 1.3, 0.5, 1], keys: [{ at: 0, t: { y: -90 } }, { at: 0.08, t: { y: -90 } }, { at: 0.2, t: 0 }, { at: 0.88, t: 0 }, { at: 1, t: { y: -90 } }] },
    balloon: { ms: 6000, ease: EASE.easeIn, keys: [{ at: 0, t: 0 }, { at: 0.5, t: { x: 8, y: -200 } }, { at: 1, t: { x: -6, y: -400 } }] },
    flame: { ms: 250, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -4 } }, { at: 1, t: { sx: 0.85, sy: 1.12, r: 4 } }] },
};
/** Task created (the card unfolds from the message, its chips pop on; 4.4 s). */
export const taskCreated = {
    card: { ms: 4400, ease: EASE.lift, keys: [{ at: 0, t: { y: -20, sy: 0.3 }, o: 0 }, { at: 0.28, t: { y: -20, sy: 0.3 }, o: 0 }, { at: 0.4, t: 0, o: 1 }, { at: 0.9, t: 0, o: 1 }, { at: 1, t: 0, o: 0 }] },
    chip: { ms: 4400, ease: EASE.ease, keys: [{ at: 0, t: { s: 0.6 }, o: 0 }, { at: 0.4, t: { s: 0.6 }, o: 0 }, { at: 0.48, t: 0, o: 1 }, { at: 0.9, t: 0, o: 1 }, { at: 1, t: 0, o: 0 }] },
};
/** The chips follow one another (0.25 s, 0.4 s, 0.55 s after the card starts). */
export const TASK_CREATED_CHIP_DELAYS_MS = [250, 400, 550];
export const TASK_CREATED_SLICE = [0.28, 0.48];
/** New support ticket (the bell rings, the badge pops, the ticket drops in; 4.4 s). */
export const newTicket = {
    bell: {
        ms: 4400,
        ease: EASE.ease,
        keys: [{ at: 0, t: 0 }, { at: 0.4, t: 0 }, { at: 0.44, t: { r: 16 } }, { at: 0.48, t: { r: -14 } }, { at: 0.52, t: { r: 10 } }, { at: 0.56, t: { r: -6 } }, { at: 0.62, t: 0 }, { at: 1, t: 0 }],
    },
    card: { ms: 4400, ease: EASE.lift, keys: [{ at: 0, t: { y: -30, s: 0.8 }, o: 0 }, { at: 0.3, t: { y: -30, s: 0.8 }, o: 0 }, { at: 0.42, t: 0, o: 1 }, { at: 0.9, t: 0, o: 1 }, { at: 1, t: 0, o: 0 }] },
};
export const NEW_TICKET_CARD_SLICE = [0.3, 0.42];
export const NEW_TICKET_BELL_SLICE = [0.4, 0.62];
/**
 * Which of a list's items arrived since it was first shown, for "new ticket" and "task created": items
 * present on first load never animate, and a batch bigger than `max` (an import, a reconnect catching up)
 * doesn't either, so a list never animates all at once.
 */
export function arrivals(seen, now, max = 3) {
    const fresh = now.filter((id) => !seen.has(id));
    return fresh.length > max ? [] : fresh;
}
/**
 * Confetti is for this person ticking one thing off. Never for undoing, for bulk changes, or for other
 * people's changes arriving live.
 */
export function celebrates(change) {
    return change.actor === 'self' && change.count === 1 && !change.wasDone && change.nowDone;
}
export * from './chapi-art.js';
// --- Chapi the painter (Showcase, SC-23; docs/adr/0067) ----------------------------------------------------
/**
 * Chapi in a beret paints the Chapsi mark on an easel while the prototype builds (Chapi_Painter.gif): the brush arm
 * dabs, the head bobs, the two leaves are painted one after the other (stroke dash, 1 = not painted yet), then a
 * sparkle, and the canvas clears for the next loop. Loops only while the build runs; reduced motion shows the finished
 * painting (`stillFrame(..., PAINTER_STILL_AT)`).
 */
export const PAINTER_LOOP_MS = 4800;
export const painter = {
    whole: { ms: 2400, ease: EASE.easeInOut, keys: [{ at: 0, t: 0 }, { at: 0.5, t: { y: -3 } }, { at: 1, t: 0 }] },
    head: { ms: 2400, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -3 } }, { at: 0.5, t: { r: 4 } }, { at: 1, t: { r: -3 } }] },
    armR: { ms: 800, ease: EASE.easeInOut, keys: [{ at: 0, t: { r: -10 } }, { at: 0.5, t: { r: 12 } }, { at: 1, t: { r: -10 } }] },
    leafA: {
        ms: PAINTER_LOOP_MS,
        ease: EASE.ease,
        keys: [{ at: 0, d: 1, o: 1 }, { at: 0.06, d: 1 }, { at: 0.38, d: 0 }, { at: 0.88, d: 0, o: 1 }, { at: 0.96, d: 0, o: 0 }, { at: 1, d: 1, o: 0 }],
    },
    leafB: {
        ms: PAINTER_LOOP_MS,
        ease: EASE.ease,
        keys: [{ at: 0, d: 1, o: 1 }, { at: 0.4, d: 1 }, { at: 0.72, d: 0 }, { at: 0.88, d: 0, o: 1 }, { at: 0.96, d: 0, o: 0 }, { at: 1, d: 1, o: 0 }],
    },
    sparkle: {
        ms: PAINTER_LOOP_MS,
        ease: EASE.easeOut,
        keys: [{ at: 0, t: { s: 0.3 }, o: 0 }, { at: 0.72, t: { s: 0.3 }, o: 0 }, { at: 0.78, t: { s: 1.1 }, o: 1 }, { at: 0.84, t: 0, o: 1 }, { at: 0.94, t: 0, o: 0 }, { at: 1, t: { s: 0.3 }, o: 0 }],
    },
};
/** Where in the loop the painting is finished: the still frame for reduced motion. */
export const PAINTER_STILL_AT = 0.84;
/** The painter's pivots, in the drawing's units (viewBox 18 -8 170 200). */
export const painterPivots = { armR: { x: 148, y: 107 }, head: { x: 106, y: 80 }, whole: { x: 100, y: 176 } };
//# sourceMappingURL=motion.js.map